import { Stack } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import type { WorkflowSignalType } from "@entrilia/shared/api/workflowTypes";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../api/adminApi";
import type { ApproverAssignment } from "../../../api/types/approvalTypes";
import type { WorkflowApproval } from "../../../api/types/workflowTypes";
import { workflowSignalTypeMap } from "../../../utilities/enumCaptions";
import ObjectDuplicatesCheck from "../../entityFields/ObjectDuplicatesCheck";
import useObjectDuplicates from "../../entityFields/useObjectDuplicates";
import ApproversAssignmentDialog from "./ApproversAssignmentDialog";
import { getInitialApproversAssignmentStateFromApprovalPreview } from "./approversAssignmentState";
import useWorkflowActionChecks from "./useWorkflowActionChecks";
import WorkflowActionChecks from "./WorkflowActionChecks";

const executeWorkflowAction = withErrorHandling(adminApi.executeWorkflowAction);

interface Props {
  workflowId: string;
  signalType: WorkflowSignalType;
  approvalPreview: WorkflowApproval;
  objectType: string;
  objectId: string;
  showActionChecks?: boolean;
  onClose: () => void;
  onActionExecuted: (signalType: WorkflowSignalType) => void;
}

const ObjectWorkflowApproversAssignmentDialog = ({
  workflowId,
  signalType,
  approvalPreview,
  objectType,
  objectId,
  showActionChecks,
  onClose,
  onActionExecuted,
}: Props) => {
  const { sendNotificationError } = useNotificationContext();

  const [executing, setExecuting] = useState(false);

  const actionChecks = useWorkflowActionChecks(showActionChecks ? { workflowId, action: signalType } : undefined);
  const duplicates = useObjectDuplicates({ objectType, objectId, action: signalType });

  const handleConfirm = async (approverAssignments: ApproverAssignment[]) => {
    setExecuting(true);
    const [resp, error] = await executeWorkflowAction(workflowId, {
      signalType,
      approverAssignments: {
        configurationVersionId: approvalPreview.approvalConfigurationVersionId,
        approverAssignments,
      },
    });
    setExecuting(false);

    if (error) {
      logError(error, "[ObjectWorkflowApproversAssignmentDialog] executeWorkflowAction");
      sendNotificationError(error.message);
      return;
    }

    if (!resp.isSuccess) {
      const failureMessages = (resp.preconditionFailures ?? []).map((f) => f.message).join("\n");
      sendNotificationError(failureMessages || resp.errorMessage || "Workflow action could not be executed");
      return;
    }

    onActionExecuted(signalType);
    onClose();
  };

  const signalAttributes = workflowSignalTypeMap.get(signalType);

  return (
    <ApproversAssignmentDialog
      initialState={getInitialApproversAssignmentStateFromApprovalPreview(approvalPreview)}
      actionName={signalAttributes?.caption || signalType}
      confirmColor={signalAttributes?.color === "error" ? "error" : "primary"}
      loading={executing || actionChecks.isFetching || duplicates.isFetching}
      disabled={actionChecks.blockers.length > 0}
      Subheader={
        <Stack spacing={2} sx={{ "&:not(:empty)": { mt: 2 } }}>
          <WorkflowActionChecks
            action={signalType}
            blockers={actionChecks.blockers}
            warnings={actionChecks.warnings}
            hasFailed={actionChecks.hasFailed}
          />
          <ObjectDuplicatesCheck objectType={objectType} objectDuplicates={duplicates} />
        </Stack>
      }
      onConfirm={handleConfirm}
      onClose={onClose}
    />
  );
};

export default ObjectWorkflowApproversAssignmentDialog;
